import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  CardQuality,
  InGameCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { withFragments } from '@sorare/core/src/lib/gql';

import xpHigh from 'assets/wheel/xp-high.webp';
import xpLow from 'assets/wheel/xp-low.webp';
import xpMedium from 'assets/wheel/xp-medium.webp';

import type { findRewardThresholdColor_probabilisticBundleSlotConfig } from './__generated__/wheelReward.graphql';

const THRESHOLD_COLORS = [
  'var(--c-yellow-700)',
  '#FF00C0',
  'var(--c-green-400)',
  'var(--c-nd-500)',
];

const rewardThresholds = [0.01, 0.02, 0.1, 1] as const;
const commonRewardThresholds = {
  shardsQuantity: [Infinity, 9999, 999, 100],
  xpQuantity: [Infinity, 9999, 4999, 999],
  cardQuality: [
    [CardQuality.TIER_0],
    [CardQuality.TIER_1],
    [CardQuality.TIER_2],
    [CardQuality.TIER_3, CardQuality.TIER_4, CardQuality.TIER_5],
  ],
};

const getThresholdColor = (thresholdIndex: number) => {
  return thresholdIndex === -1
    ? 'var(--c-nd-500)'
    : THRESHOLD_COLORS[thresholdIndex];
};

export const findRewardThresholdColor = withFragments(
  (
    slotConfig: findRewardThresholdColor_probabilisticBundleSlotConfig,
    rarity: Rarity
  ) => {
    const { probability } = slotConfig;
    if (rarity === Rarity.common) {
      if (slotConfig.deliverableItems?.at(0)?.quantity) {
        return THRESHOLD_COLORS[0];
      }
      const cardQuality = slotConfig.cards?.at(0)?.quality;
      if (cardQuality) {
        return getThresholdColor(
          commonRewardThresholds.cardQuality.findIndex(q =>
            (q as CardQuality[]).includes(cardQuality)
          )
        );
      }
      const xpQuantity = slotConfig.inGameCurrencies.find(
        igc => igc.currency === InGameCurrency.COMMON_XP
      )?.amount;
      if (xpQuantity) {
        return getThresholdColor(
          commonRewardThresholds.xpQuantity.findIndex(
            (q, index) =>
              q >= xpQuantity &&
              (index === commonRewardThresholds.xpQuantity.length - 1 ||
                xpQuantity > commonRewardThresholds.xpQuantity[index + 1])
          )
        );
      }
      const shardsQuantity = slotConfig.cardShards.at(0)?.quantity;
      if (shardsQuantity) {
        return getThresholdColor(
          commonRewardThresholds.shardsQuantity.findIndex(
            (q, index) =>
              q >= shardsQuantity &&
              (index === commonRewardThresholds.shardsQuantity.length - 1 ||
                shardsQuantity >
                  commonRewardThresholds.shardsQuantity[index + 1])
          )
        );
      }
    }
    return getThresholdColor(rewardThresholds.findIndex(t => probability < t));
  },
  {
    probabilisticBundleSlotConfig: gql`
      fragment findRewardThresholdColor_probabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
        id
        probability
        cardShards {
          id
          quantity
        }
        deliverableItems {
          quantity
        }
        cards {
          quality
        }
        inGameCurrencies {
          id
          currency
          amount
        }
      }
    ` as TypedDocumentNode<findRewardThresholdColor_probabilisticBundleSlotConfig>,
  }
);

export const getXpIllustration = (xp: number) => {
  if (xp < 1000) {
    return xpLow;
  }
  if (xp < 2000) {
    return xpMedium;
  }
  return xpHigh;
};
